import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import {
    ErrorOutline as CriticalIcon,
    HelpOutline as NotAssessedIcon,
    InfoOutlined as PreferenceIcon,
    WarningAmber as MaterialIcon,
} from "@mui/icons-material";
import type { Finding, FindingDecision, RunDocumentParagraph, Run, Severity } from "../../services/lensService";
import useRunDocument from "./hooks/useRunDocument";
import FindingCard from "./FindingCard";
import { isNotAssessed } from "./lensUtils";
import {
    type ParagraphMarks,
    type Placement,
    SEVERITY_LABEL,
    SEVERITY_RANK,
    isHeadingLike,
    layoutFindings,
    segment,
} from "./sideBySide";

const SEVERITY_ICON: Record<Severity, typeof CriticalIcon> = {
    critical: CriticalIcon,
    material: MaterialIcon,
    preference: PreferenceIcon,
};

// Underline style differs by severity too, so a highlight is told apart without its colour.
const UNDERLINE: Record<Severity, "solid" | "dashed" | "dotted"> = { critical: "solid", material: "dashed", preference: "dotted" };

const useStyles = makeStyles()((theme) => {
    const tone = (severity: Severity) => {
        const colour = theme.palette[severity === "critical" ? "error" : severity === "material" ? "warning" : "info"];
        return {
            mark: {
                backgroundColor: alpha(colour.main, 0.2),
                textDecorationLine: "underline",
                textDecorationStyle: UNDERLINE[severity],
                textDecorationColor: colour.main,
                textDecorationThickness: 2,
            },
            paragraph: { backgroundColor: alpha(colour.main, 0.07), borderLeftColor: colour.main },
            badge: { borderColor: colour.main, color: colour.dark },
        };
    };
    const critical = tone("critical");
    const material = tone("material");
    const preference = tone("preference");

    return {
        grid: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)",
            gap: theme.spacing(2),
            [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" },
        },
        pane: { height: "calc(100vh - 180px)", minHeight: 480, overflowY: "auto", padding: theme.spacing(3) },
        paragraph: {
            marginBottom: theme.spacing(1.25),
            padding: theme.spacing(0.25, 1),
            borderLeft: "3px solid transparent",
            borderRadius: 2,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
        },
        heading: { fontWeight: 700, marginTop: theme.spacing(2) },
        title: { fontWeight: 700, fontSize: "1.2rem", textAlign: "center" },
        number: { display: "inline-block", minWidth: "2.5em", marginRight: theme.spacing(0.5) },
        notAssessedSection: {
            borderLeft: "3px dashed #999",
            backgroundImage: "repeating-linear-gradient(135deg, transparent 0 10px, rgba(0,0,0,0.025) 10px 20px)",
        },
        criticalParagraph: critical.paragraph,
        materialParagraph: material.paragraph,
        preferenceParagraph: preference.paragraph,
        mark: {
            color: "inherit",
            cursor: "pointer",
            borderRadius: 2,
            textUnderlineOffset: 3,
            "&:hover": { filter: "brightness(0.95)" },
        },
        criticalMark: critical.mark,
        materialMark: material.mark,
        preferenceMark: preference.mark,
        declinedMark: { opacity: 0.55 },
        selected: { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
        badge: {
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            marginRight: theme.spacing(0.75),
            padding: "0 6px",
            border: "1px solid",
            borderRadius: 10,
            backgroundColor: theme.palette.background.paper,
            fontSize: 11,
            fontWeight: 700,
            lineHeight: "18px",
            textTransform: "uppercase",
            verticalAlign: 1,
            cursor: "pointer",
            fontFamily: "inherit",
            "& svg": { fontSize: 13 },
        },
        criticalBadge: critical.badge,
        materialBadge: material.badge,
        preferenceBadge: preference.badge,
        notAssessedBadge: { borderColor: "#999", borderStyle: "dashed", color: "#666" },
        group: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: "#777",
            margin: theme.spacing(0, 0, 1),
        },
        groupAfter: { marginTop: theme.spacing(3) },
        item: { cursor: "pointer", borderRadius: 4, marginBottom: theme.spacing(2) },
        itemSelected: { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        where: { fontSize: 12, color: "#777", marginBottom: theme.spacing(0.5) },
        unlocated: { color: theme.palette.warning.dark, fontStyle: "italic" },
        center: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
        empty: { color: "#999", padding: theme.spacing(2, 0) },
    };
});

const WHERE: Record<Placement["kind"], string> = {
    text: "Highlighted in the document",
    paragraph: "Whole paragraph highlighted",
    heading: "Placed at its section heading",
    section: "Section marked in the document",
    unlocated: "Couldn't locate in document",
};

const badgeLabel = (finding: Finding) => {
    if (isNotAssessed(finding)) return "Not assessed";
    const decided: Record<FindingDecision, string> = { undecided: "", accepted: " · accepted", declined: " · declined" };
    return `${SEVERITY_LABEL[finding.severity]}${decided[finding.decision]}`;
};

interface ParagraphProps {
    paragraph: RunDocumentParagraph;
    marks: ParagraphMarks | undefined;
    findingsByKey: Map<string, Finding>;
    // Only set when the selected finding is on this paragraph, so the others do not re-render on selection.
    selectedKey: string | null;
    onSelect: (key: string) => void;
    onSelectAmong: (keys: string[]) => void;
}

const Paragraph = memo(({ paragraph, marks, findingsByKey, selectedKey, onSelect, onSelectAmong }: ParagraphProps) => {
    const { classes, cx } = useStyles();
    const isTitle = paragraph.style === "Title";
    const isHeading = !isTitle && isHeadingLike(paragraph);

    // The most severe finding placed on the whole paragraph gives it its tone.
    const wholeFinding = marks?.wholeParagraph
        ? marks.badges.find((f) => !isNotAssessed(f) && marks.textMarks.every((m) => m.findingKey !== f.key))
        : undefined;
    const severityOf = (key: string) => {
        const finding = findingsByKey.get(key);
        return finding ? SEVERITY_RANK[finding.severity] : 9;
    };
    const segments = segment(paragraph.text, marks?.textMarks ?? [], severityOf);

    return (
        <div
            data-paragraph={paragraph.index}
            className={cx(
                classes.paragraph,
                isHeading && classes.heading,
                isTitle && classes.title,
                marks?.notAssessed && classes.notAssessedSection,
                wholeFinding && classes[`${wholeFinding.severity}Paragraph`],
                wholeFinding && selectedKey === wholeFinding.key && classes.selected,
            )}
        >
            {marks?.badges.map((finding) => {
                const Icon = isNotAssessed(finding) ? NotAssessedIcon : SEVERITY_ICON[finding.severity];
                return (
                    <button
                        key={finding.key}
                        type="button"
                        data-finding={finding.key}
                        title={finding.provisionName}
                        className={cx(
                            classes.badge,
                            isNotAssessed(finding) ? classes.notAssessedBadge : classes[`${finding.severity}Badge`],
                            selectedKey === finding.key && classes.selected,
                        )}
                        onClick={() => onSelect(finding.key)}
                    >
                        <Icon />
                        {badgeLabel(finding)}
                    </button>
                );
            })}
            {paragraph.number && <span className={classes.number}>{paragraph.number}</span>}
            {segments.map((part, i) => {
                if (part.findingKeys.length === 0) return <span key={i}>{part.text}</span>;
                const top = findingsByKey.get(part.findingKeys[0]);
                if (!top) return <span key={i}>{part.text}</span>;
                const allDeclined = part.findingKeys.every((key) => findingsByKey.get(key)?.decision === "declined");
                return (
                    <mark
                        key={i}
                        role="button"
                        tabIndex={0}
                        data-findings={part.findingKeys.join(" ")}
                        aria-label={`${SEVERITY_LABEL[top.severity]}: ${top.provisionName}`}
                        className={cx(
                            classes.mark,
                            classes[`${top.severity}Mark`],
                            allDeclined && classes.declinedMark,
                            selectedKey != null && part.findingKeys.includes(selectedKey) && classes.selected,
                        )}
                        onClick={() => onSelectAmong(part.findingKeys)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onSelectAmong(part.findingKeys);
                            }
                        }}
                    >
                        {part.text}
                    </mark>
                );
            })}
        </div>
    );
});

// Brings the target into view inside its own pane, leaving the page where it is.
const scrollWithin = (pane: HTMLElement | null, target: Element | null | undefined) => {
    if (!pane || !target) return;
    const paneBox = pane.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    if (box.top >= paneBox.top && box.bottom <= paneBox.bottom) return;
    pane.scrollTo({ top: pane.scrollTop + box.top - paneBox.top - pane.clientHeight / 3, behavior: "smooth" });
};

interface RunSideBySideProps {
    run: Run;
    findings: Finding[];
    decidingKey: string | null;
    onDecide: (findingKey: string, decision: FindingDecision) => void;
}

const RunSideBySide = ({ run, findings, decidingKey, onDecide }: RunSideBySideProps) => {
    const { classes, cx } = useStyles();
    const isUpload = run.source === "upload";
    const runDocument = useRunDocument(run.identifier, isUpload);
    const documentPane = useRef<HTMLDivElement>(null);
    const findingsPane = useRef<HTMLDivElement>(null);
    const [selected, setSelected] = useState<{ key: string; from: "document" | "list" } | null>(null);

    const paragraphs = useMemo(() => runDocument.data?.paragraphs ?? [], [runDocument.data]);
    const layout = useMemo(() => layoutFindings(paragraphs, findings), [paragraphs, findings]);
    const findingsByKey = useMemo(() => new Map(findings.map((f) => [f.key, f])), [findings]);
    const placements = useRef(layout.placements);
    placements.current = layout.placements;

    const selectedKey = selected?.key ?? null;
    const selectFromDocument = useCallback((key: string) => setSelected({ key, from: "document" }), []);
    const selectAmong = useCallback(
        (keys: string[]) =>
            setSelected((current) => {
                const at = current ? keys.indexOf(current.key) : -1;
                return { key: keys[(at + 1) % keys.length], from: "document" };
            }),
        [],
    );

    useEffect(() => {
        if (!selected) return;
        if (selected.from === "document") {
            scrollWithin(findingsPane.current, findingsPane.current?.querySelector(`[data-item="${selected.key}"]`));
            return;
        }
        const placement = placements.current[selected.key];
        if (!placement || placement.kind === "unlocated") return;
        const pane = documentPane.current;
        scrollWithin(
            pane,
            pane?.querySelector(`[data-findings~="${selected.key}"]`) ?? pane?.querySelector(`[data-paragraph="${placement.index}"]`),
        );
    }, [selected]);

    if (!isUpload)
        return <Alert severity="info">Side by side isn&rsquo;t available for Word add-in runs.</Alert>;

    if (runDocument.isLoading)
        return (
            <Paper className={classes.center}>
                <CircularProgress />
            </Paper>
        );

    if (!runDocument.data?.available)
        return (
            <Alert severity="warning">
                {runDocument.isError ? "The document for this run could not be loaded." : "The document for this run isn't available."}
            </Alert>
        );

    const item = (finding: Finding) => {
        const placement = layout.placements[finding.key];
        return (
            <Box
                key={finding.key}
                data-item={finding.key}
                className={cx(classes.item, selectedKey === finding.key && classes.itemSelected)}
                onClick={() => setSelected({ key: finding.key, from: "list" })}
            >
                {placement && (
                    <Typography component="div" className={cx(classes.where, placement.kind === "unlocated" && classes.unlocated)}>
                        {WHERE[placement.kind]}
                    </Typography>
                )}
                <FindingCard
                    compact
                    finding={finding}
                    isDeciding={decidingKey === finding.key}
                    onDecide={(decision) => onDecide(finding.key, decision)}
                />
            </Box>
        );
    };

    return (
        <Box className={classes.grid}>
            <Paper variant="outlined" className={classes.pane} ref={documentPane} aria-label="Original document">
                {paragraphs.map((paragraph) => {
                    const marks = layout.marks.get(paragraph.index);
                    return (
                        <Paragraph
                            key={paragraph.index}
                            paragraph={paragraph}
                            marks={marks}
                            findingsByKey={findingsByKey}
                            selectedKey={selectedKey && marks?.badges.some((f) => f.key === selectedKey) ? selectedKey : null}
                            onSelect={selectFromDocument}
                            onSelectAmong={selectAmong}
                        />
                    );
                })}
            </Paper>
            <Box className={classes.pane} ref={findingsPane} aria-label="Findings">
                {layout.omissions.length > 0 && (
                    <>
                        <Typography component="h3" className={classes.group}>
                            Missing clauses ({layout.omissions.length})
                        </Typography>
                        {layout.omissions.map((finding) => (
                            <Box key={finding.key} data-item={finding.key} className={classes.item}>
                                <FindingCard
                                    compact
                                    finding={finding}
                                    isDeciding={decidingKey === finding.key}
                                    onDecide={(decision) => onDecide(finding.key, decision)}
                                />
                            </Box>
                        ))}
                    </>
                )}
                <Typography component="h3" className={cx(classes.group, layout.omissions.length > 0 && classes.groupAfter)}>
                    In the document ({layout.ordered.length})
                </Typography>
                {layout.ordered.length === 0 ? (
                    <Typography variant="body2" className={classes.empty}>
                        {findings.length === 0 ? "No findings match the selected filters." : "No findings in the document itself."}
                    </Typography>
                ) : (
                    layout.ordered.map(item)
                )}
            </Box>
        </Box>
    );
};

export default RunSideBySide;
